//Global imports
import { useEffect, useState } from 'react';
import { CCol, CFormCheck, CRow } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilFolder } from '@coreui/icons';

//File imports
import { DraftService } from './Api/DraftService';
import Notification from '../../services/Notification/Notification';
import { Language } from '../../language/English';
import Logger from '../../services/Logger/Logger';
import DraftResultsPojo from './Api/DraftResultPojo';
import { DraftPojo } from './Api/DraftPojo';

//Component imports
import { Layout } from '../../components/Layout';
import Loader from '../../components/Layout/Loader';
import { DraftCard } from './Components';
import {
    PAGINATION_DEFAULT_LIMIT,
    PAGINATION_DEFAULT_PAGE,
} from '../../Constant';

const Drafts = () => {
    const draftService = new DraftService();
    const notification = new Notification();
    const logger = new Logger();

    const [showLoader, setShowLoader] = useState<boolean>(true);
    const [draftList, setDraftList] = useState<DraftResultsPojo[][] | null>(
        null,
    );
    const [page, setPage] = useState<number>(PAGINATION_DEFAULT_PAGE);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [showDevDrafts, setShowDevDrafts] = useState<boolean>(false);

    useEffect(() => {
        getAllDraftDocuments(page);
    }, [page, showDevDrafts]);

    const getAllDraftDocuments = async (page: number) => {
        setIsLoadingMore(true);
        try {
            const response = await draftService.getAllWithPagination(
                page,
                PAGINATION_DEFAULT_LIMIT,
                showDevDrafts,
            );
            if (response && response.isSuccess) {
                const chunkedDrafts: any = [];
                const rawDrafts = response?.drafts.data || [];
                for (let i = 0; i < rawDrafts.length; i += 2) {
                    chunkedDrafts.push(rawDrafts.slice(i, i + 2));
                }

                if (page === PAGINATION_DEFAULT_PAGE) {
                    setDraftList(chunkedDrafts);
                } else {
                    setDraftList((prev: DraftResultsPojo[][] | null) => [
                        ...(prev ?? []),
                        ...chunkedDrafts,
                    ]);
                }

                const totalFetched =
                    (page - 1) * PAGINATION_DEFAULT_LIMIT + rawDrafts.length;

                if (totalFetched >= response?.drafts?.totalRecord) {
                    setHasMore(false);
                } else {
                    setHasMore(true);
                }
            } else {
                notification.error(Language.CANNOT_LOAD_DOCUMENT_DRAFTS_ERROR);
            }
            setShowLoader(false);
            setIsLoadingMore(false);
        } catch (e) {
            notification.error(Language.SOMETHING_IS_WRONG);
            setShowLoader(false);
            setIsLoadingMore(false);
            logger.error(e);
        }
    };

    const handleLoadMore = () => {
        setPage((prev) => prev + 1);
    };

    const handleShowDevToggle = (checked: boolean) => {
        setShowDevDrafts(checked);
        setDraftList(null);
        setHasMore(true);
        setPage(PAGINATION_DEFAULT_PAGE);
        setShowLoader(true);
    };

    return (
        <Layout>
            {showLoader ? (
                <Loader />
            ) : (
                <>
                    <CRow className="mb-2 align-items-center">
                        <CCol xs="auto">
                            <CFormCheck
                                id="showDevDocs"
                                reverse
                                checked={showDevDrafts}
                                label={Language.SHOW_DEV_DOCUMENTS}
                                className="fw-semibold border-black-checkbox"
                                onChange={(e) =>
                                    handleShowDevToggle(e.target.checked)
                                }
                            />
                        </CCol>
                    </CRow>
                    {draftList && draftList.length > 0 ? (
                        <>
                            <CRow>
                                {draftList.map((row: any, rowIndex) =>
                                    row.map((draftData: DraftPojo) => (
                                        <DraftCard
                                            key={draftData.documentId}
                                            draft={draftData}
                                        />
                                    )),
                                )}
                            </CRow>

                            {hasMore && (
                                <div className="text-center mb-4">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleLoadMore}
                                        disabled={isLoadingMore}
                                    >
                                        {isLoadingMore
                                            ? Language.LOADING_
                                            : Language.LOADMORE}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : draftList &&
                      Array.isArray(draftList) &&
                      draftList.length === 0 ? (
                        <div
                            className="d-flex flex-column align-items-center justify-content-center"
                            style={{ minHeight: '60vh' }}
                        >
                            <CIcon
                                icon={cilFolder}
                                size="3xl"
                                className="mb-3 text-secondary"
                            />
                            <h4 className="mb-3 text-center text-secondary fs-6">
                                {Language.NO_DRAFT_DOCUMENTS} <br />
                            </h4>
                        </div>
                    ) : null}
                </>
            )}
        </Layout>
    );
};

export default Drafts;
